import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Mic, MicOff, Volume2, Phone, Navigation, Check, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export const VoiceAssistant = ({ orderId, onAction, userType = 'customer' }) => {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [lastCommand, setLastCommand] = useState(null);
  const [error, setError] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const { token } = useAuth();

  const startListening = async () => {
    setError('');
    setTranscription('');
    setLastCommand(null);

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Este navegador não suporta gravação de voz. Use o Chrome ou o Safari atualizado.');
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setError('Não foi possível aceder ao microfone. Autorize o microfone nas definições do navegador.');
      console.error('Microphone error:', err);
      return;
    }

    const mimeType = pickMimeType();
    const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunksRef.current.push(event.data);
    };

    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach(track => track.stop());
      const type = mediaRecorder.mimeType || mimeType || 'audio/webm';
      await processAudio(new Blob(audioChunksRef.current, { type }), type);
    };

    mediaRecorder.start();
    setIsListening(true);

    setTimeout(() => {
      if (mediaRecorderRef.current?.state === 'recording') stopListening();
    }, 10000);
  };

  const stopListening = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
  };

  const processAudio = async (audioBlob, mimeType) => {
    setIsProcessing(true);
    try {
      const base64Audio = await blobToDataUrl(audioBlob);
      const response = await axios.post(`${API}/ai/voice-command`, {
        audio: base64Audio,
        mime_type: mimeType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const command = response.data.command || { action: 'unknown' };
      setTranscription(response.data.transcription);
      setLastCommand(command);

      if (command.action && command.action !== 'unknown' && onAction) {
        await onAction(command, orderId);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao processar comando de voz. Tente novamente.');
      console.error('Voice processing error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const getCommandIcon = (action) => {
    switch (action) {
      case 'notify_on_way': return <Navigation className="w-5 h-5" />;
      case 'call_customer': return <Phone className="w-5 h-5" />;
      case 'mark_delivered': return <Check className="w-5 h-5" />;
      case 'mark_picked_up': return <Check className="w-5 h-5" />;
      default: return <Volume2 className="w-5 h-5" />;
    }
  };

  const getCommandLabel = (action) => {
    const labels = {
      'notify_on_way': 'A notificar cliente...',
      'call_customer': 'A ligar para cliente...',
      'mark_delivered': 'A marcar como entregue...',
      'mark_picked_up': 'A marcar como recolhido...',
      'accept_order': 'A aceitar pedido...',
      'create_order': 'A criar pedido...',
      'clarify': 'Preciso de mais detalhes',
      'unknown': 'Comando não reconhecido'
    };
    return labels[action] || action;
  };

  return (
    <div className="bg-white rounded-2xl p-4 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Volume2 className="w-5 h-5 text-tuggo-mint" />
          <span className="font-semibold text-gray-900">Assistente de Voz</span>
        </div>
        {userType === 'driver' && (
          <span className="text-xs bg-tuggo-mint/10 text-tuggo-mint px-2 py-1 rounded-full">
            Mãos livres
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg mb-4" data-testid="voice-error">
          {error}
        </div>
      )}

      {/* Voice Button */}
      <div className="flex justify-center mb-4">
        <button
          onClick={isListening ? stopListening : startListening}
          disabled={isProcessing}
          className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
            isListening 
              ? 'bg-red-500 animate-pulse' 
              : isProcessing 
                ? 'bg-gray-300' 
                : 'bg-tuggo-mint hover:bg-tuggo-mint/90'
          }`}
          data-testid="voice-button"
        >
          {isProcessing ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : isListening ? (
            <MicOff className="w-8 h-8 text-white" />
          ) : (
            <Mic className="w-8 h-8 text-white" />
          )}
        </button>
      </div>

      <p className="text-center text-sm text-gray-500 mb-4">
        {isListening ? 'A ouvir... Fale agora' : isProcessing ? 'A processar...' : 'Toque para falar'}
      </p>

      {/* Transcription */}
      {transcription && (
        <div className="bg-gray-50 rounded-xl p-3 mb-4" data-testid="voice-transcription">
          <div className="text-xs text-gray-500 mb-1">Transcrição:</div>
          <div className="text-gray-900">"{transcription}"</div>
        </div>
      )}

      {lastCommand?.action === 'unknown' && (
        <div className="bg-yellow-50 text-yellow-800 text-sm rounded-xl p-3" data-testid="voice-unknown">
          Não percebi o comando. Experimente dizer: "Estou a caminho", "Encomenda recolhida", "Entrega concluída" ou "Ligar ao cliente".
        </div>
      )}

      {/* Command Result */}
      {lastCommand && lastCommand.action !== 'unknown' && (
        <div className={`rounded-xl p-3 flex items-center gap-3 ${
          lastCommand.action === 'clarify' ? 'bg-yellow-50' : 'bg-green-50'
        }`} data-testid="voice-command-result">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            lastCommand.action === 'clarify' ? 'bg-yellow-100' : 'bg-green-100'
          }`}>
            {getCommandIcon(lastCommand.action)}
          </div>
          <div>
            <div className="font-medium text-gray-900">{getCommandLabel(lastCommand.action)}</div>
            {lastCommand.message && (
              <div className="text-sm text-gray-600">{lastCommand.message}</div>
            )}
          </div>
        </div>
      )}

      {/* Quick Commands for Drivers */}
      {userType === 'driver' && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-500 mb-2">Comandos rápidos:</div>
          <div className="text-xs text-gray-400 space-y-1">
            <p>• "Estou a caminho"</p>
            <p>• "Ligar para cliente"</p>
            <p>• "Encomenda recolhida"</p>
            <p>• "Entrega concluída"</p>
          </div>
        </div>
      )}
    </div>
  );
};
